import {
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import {
  textFieldValidation,
  numberFieldValidation,
} from '@/utils/validations';
import { Modal } from '@/components';
import PriceInput from '@/components/inputControls/PriceInput';
import { ModalProps } from '@mui/base/Modal';
import { IPromoData, DiscountType } from '@/types/promo-management.types';
import { Statuses, CouponType, CouponEligibilityRule } from '@/constant';
import { useGetYachtsQuery } from '@/services/YatchManagementService';
import NumberInput from '@/components/inputControls/NumberInput';
import { formatDisplayDate } from '@/utils/Date';

interface HTMLInputElementWithPreventWheel extends HTMLInputElement {
  _preventWheel?: (event: WheelEvent) => void;
}

interface PromoModalProps extends Omit<ModalProps, 'children'> {
  mode: 'view' | 'edit' | 'create' | 'details';
  formData: Partial<IPromoData>;
  setFormData: Dispatch<SetStateAction<Partial<IPromoData>>>;
  errors: { [key: string]: string };
  setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onSubmit: () => Promise<void>;
  isSubmitting?: boolean;
}

const PromoModal = ({
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  open,
  onClose,
  className,
}: PromoModalProps) => {
  const nameInputRef = useRef<HTMLInputElement>(null);

  const { data: clubsData, isLoading: isLoadingClubs } = useGetYachtsQuery({
    page: 1,
    limit: 1000,
  });
  const clubs = clubsData?.data ?? [];

  const validateField = useCallback(
    async (name: keyof IPromoData | string, value: unknown) => {
      try {
        const schema = Yup.object().shape({
          name: textFieldValidation('Coupon Name', 2, 100, true),
          code: textFieldValidation('Coupon Code', 2, 50, true),
          description: textFieldValidation('Description', 0, 500, false),
          coupon_type: Yup.number().required('Coupon type is required'),
          max_discount: Yup.number().when('discount_type', {
            is: (value: string | number) =>
              Number(value) === DiscountType.PERCENTAGE,
            then: (schema) =>
              schema
                .required(
                  'Max discount amount is required for percentage discounts'
                )
                .positive('Must be greater than 0'),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          min_order_value: numberFieldValidation(
            'Min Order Value',
            0,
            100000,
            true
          ),
          discount_amount: Yup.number().when('discount_type', {
            is: (value: string | number) =>
              Number(value) === DiscountType.FIXED_AMOUNT,
            then: (schema) =>
              schema
                .required('Discount amount is required')
                .positive('Must be greater than 0'),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          discount_percentage: Yup.number().when('discount_type', {
            is: (value: string | number) =>
              Number(value) === DiscountType.PERCENTAGE,
            then: (schema) =>
              schema
                .required('Discount percentage is required')
                .positive('Must be greater than 0')
                .max(100, 'Cannot exceed 100%'),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          total_quantity: numberFieldValidation(
            'Total Quantity',
            1,
            1000000,
            false
          ),
          remaining_usage_count: numberFieldValidation(
            'Remaining Usage Count',
            0,
            1000000,
            false
          ),
          customer_usage_limit: numberFieldValidation(
            'Customer Usage Limit',
            1,
            1000,
            false
          ),
          start_date: Yup.string()
            .required('Start date is required')
            .test(
              'is-valid-date',
              'Please enter a valid date and time',
              function (startDate) {
                if (!startDate) return false;
                const date = new Date(startDate);
                return !isNaN(date.getTime());
              }
            ),
          end_date: Yup.string()
            .required('End date is required')
            .test(
              'is-valid-date',
              'Please enter a valid date and time',
              function (endDate) {
                if (!endDate) return false;
                const date = new Date(endDate);
                return !isNaN(date.getTime());
              }
            )
            .test(
              'is-after-start-date',
              'End date must be after start date',
              function (endDate) {
                const startDate = this.parent.start_date;
                if (!startDate || !endDate) return true;
                const startDateTime = new Date(startDate);
                const endDateTime = new Date(endDate);
                return endDateTime > startDateTime;
              }
            ),
          discount_type: Yup.number().required('Discount type is required'),
          status: Yup.number().required('Status is required'),
          is_restricted_to_groups: Yup.boolean().notRequired(),
          is_supplier_promotion: Yup.boolean().notRequired(),
          frequency: Yup.number().notRequired().nullable(),
        });

        await schema.validateAt(
          name as string,
          { ...formData, [name]: value },
          { abortEarly: true }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => {
            const newError = err.errors[0] || 'Invalid input';
            if (prev[name] === newError) return prev;
            return { ...prev, [name]: newError };
          });
        }
      }
    },
    [setErrors, formData]
  );

  // Focus the Promo Name field when modal opens (not in view mode)
  useEffect(() => {
    if (open && mode !== 'view') {
      setTimeout(() => nameInputRef.current?.focus(), 0);
    }
  }, [open, mode]);

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value, type } = e.target;
      const key = name as keyof IPromoData;

      let processedValue: string | number | boolean | undefined = value;
      if (type === 'checkbox') {
        processedValue = (e.target as HTMLInputElement).checked;
      } else if (type === 'number') {
        processedValue = value === '' ? undefined : Number(value);
      } else if (
        type === 'date' &&
        (name === 'start_date' || name === 'end_date')
      ) {
        // For date inputs, append 12:00 AM (00:00) to the selected date
        processedValue = value ? `${value}T00:00:00.000Z` : value;
      } else if (name === 'code') {
        // Convert coupon code to uppercase
        processedValue = value.toUpperCase();
      } else if (
        name === 'status' ||
        name === 'discount_type' ||
        name === 'coupon_type' ||
        name === 'eligibility_rule'
      ) {
        // Convert select values to numbers for status, discount_type, coupon_type, and eligibility_rule
        processedValue = Number(value);
      }

      setFormData((prev: Partial<IPromoData>) => {
        const updatedData: Partial<IPromoData> = {
          ...prev,
          [key]: processedValue,
          // Clear appropriate discount field when discount type changes
          ...(key === 'discount_type' && {
            discount_amount:
              processedValue === DiscountType.FIXED_AMOUNT
                ? prev.discount_amount
                : undefined,
            discount_percentage:
              processedValue === DiscountType.PERCENTAGE
                ? prev.discount_percentage
                : undefined,
          }),
        };

        if (key === 'coupon_type') {
          const nextCouponType = Number(processedValue);
          if (nextCouponType !== CouponType.SUBSCRIPTION_BOATER) {
            updatedData.eligibility_rule = CouponEligibilityRule.NONE;
            updatedData.linked_club_id = undefined;
          }
        }

        if (key === 'eligibility_rule') {
          const nextEligibilityRule = Number(processedValue);
          if (nextEligibilityRule !== CouponEligibilityRule.CLUB_MEMBER_ONLY) {
            updatedData.linked_club_id = undefined;
          }
        }
        return updatedData;
      });

      // Validate the current field
      validateField(key, processedValue);

      // If start_date changes, also validate end_date to check the relationship
      if (key === 'start_date' && formData.end_date) {
        setTimeout(() => {
          validateField('end_date', formData.end_date);
        }, 0);
      }
    },
    [setFormData, validateField, formData.end_date]
  );

  const handleBlur = useCallback(
    (name: keyof IPromoData) => {
      const value = formData[name];
      validateField(name, value);
    },
    [formData, validateField]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ' ' && e.currentTarget.value === '') {
        e.preventDefault();
      }
    },
    []
  );

  const handleDateKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Prevent all keyboard input except Tab, Enter, and Escape for navigation
      if (!['Tab', 'Enter', 'Escape'].includes(e.key)) {
        e.preventDefault();
      }
    },
    []
  );

  // Prevent wheel events on number inputs
  useEffect(() => {
    const preventWheel = (e: WheelEvent) => e.preventDefault();

    const numberInputs = document.querySelectorAll(
      'input[inputmode="decimal"]'
    ) as NodeListOf<HTMLInputElementWithPreventWheel>;
    numberInputs.forEach((input) => {
      if (!input._preventWheel) {
        input._preventWheel = preventWheel;
        input.addEventListener('wheel', preventWheel, { passive: false });
      }
    });

    return () => {
      numberInputs.forEach((input) => {
        if (input._preventWheel) {
          input.removeEventListener('wheel', input._preventWheel);
          delete input._preventWheel;
        }
      });
    };
  }, [open]);

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return 'Create New Coupon Code';
      case 'edit':
        return 'Edit Coupon Code';
      case 'view':
        return 'View Coupon Code';
      case 'details':
        return 'Coupon Code Details';
      default:
        return 'Coupon Code';
    }
  };

  const getDiscountTypeLabel = (type: DiscountType) => {
    switch (type) {
      case DiscountType.PERCENTAGE:
        return 'Percentage';
      case DiscountType.FIXED_AMOUNT:
        return 'Fixed Amount';
      default:
        return 'Unknown';
    }
  };

  const getStatusLabel = (status: Statuses) => {
    switch (status) {
      case Statuses.ACTIVE:
        return 'Active';
      case Statuses.INACTIVE:
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  const getCouponTypeLabel = (type?: number) => {
    switch (type) {
      case CouponType.SUBSCRIPTION_BOATER:
        return 'Subscription - Boater';
      case CouponType.SUBSCRIPTION_HARBOR:
        return 'Subscription - Harbor';
      case CouponType.SUBSCRIPTION_CLUB:
        return 'Subscription - Club';
      case CouponType.SUBSCRIPTION_DMO:
        return 'Subscription - DMO';
      default:
        return 'Not specified';
    }
  };

  // Convert ISO or arbitrary date string to input[type="date"] format (YYYY-MM-DD)
  const formatDateLocal = (dateString?: string) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleCloseModal = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="promo-modal-title"
    >
      <div className="bg-gray-100 rounded-lg shadow-xl w-full max-w-4xl mx-4 h-[90dvh] max-h-[950px] overflow-y-hidden">
        <div className='h-full overflow-auto'>
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h1
              id="promo-modal-title"
              className="text-lg font-semibold text-gray-900"
            >
              {getModalTitle()}
            </h1>
            <button
              className="text-gray-400 hover:text-gray-600 transition-colors"
              onClick={handleCloseModal}
              type="button"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Coupon Name */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Coupon Name <span className="text-red-500">*</span>
                </label>
                <input
                  ref={nameInputRef}
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('name')}
                  onKeyDown={handleKeyDown}
                  disabled={isSubmitting || mode === 'view'}
                  className={clsx(
                    'input',
                    errors.name && 'border-red-500 focus:ring-red-500'
                  )}
                  placeholder="Enter coupon name"
                  aria-label="Coupon Name"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <span id="name-error" className="text-2sm text-danger">
                    {errors.name}
                  </span>
                )}
              </div>

              {/* Coupon Code */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="code"
                  className="text-sm font-medium text-gray-700"
                >
                  Coupon Code <span className="text-red-500">*</span>
                </label>
                <input
                  id="code"
                  type="text"
                  name="code"
                  value={formData.code || ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('code')}
                  disabled={isSubmitting || mode === 'view'}
                  style={{ textTransform: 'uppercase' }}
                  className={clsx(
                    'input',
                    errors.code && 'border-red-500 focus:ring-red-500'
                  )}
                  placeholder="Enter coupon code"
                  aria-label="Coupon Code"
                  aria-invalid={!!errors.code}
                  aria-describedby={errors.code ? 'code-error' : undefined}
                />
                {errors.code && (
                  <span id="code-error" className="text-2sm text-danger">
                    {errors.code}
                  </span>
                )}
              </div>

              {/* Description */}
              <div className="flex flex-col gap-2 md:col-span-2">
                <label
                  htmlFor="description"
                  className="text-sm font-medium text-gray-700"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('description')}
                  disabled={isSubmitting || mode === 'view'}
                  className={clsx(
                    'textarea resize-none',
                    errors.description && 'border-red-500 focus:ring-red-500'
                  )}
                  placeholder="Enter description"
                  rows={3}
                  aria-label="Description"
                  aria-invalid={!!errors.description}
                  aria-describedby={
                    errors.description ? 'description-error' : undefined
                  }
                />
                {errors.description && (
                  <span id="description-error" className="text-2sm text-danger">
                    {errors.description}
                  </span>
                )}
              </div>

              {/* Discount Type */}
              <div className="flex flex-col gap-2">
                {mode === 'view' ? (
                  <>
                    <span
                      id="discount_type-label"
                      className="text-sm font-medium text-gray-700"
                    >
                      Discount Type <span className="text-red-500">*</span>
                    </span>
                    <div
                      id="discount_type"
                      className="input bg-gray-100"
                      aria-labelledby="discount_type-label"
                      role="text"
                    >
                      {getDiscountTypeLabel(
                        formData.discount_type || DiscountType.FIXED_AMOUNT
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="discount_type"
                      className="text-sm font-medium text-gray-700"
                    >
                      Discount Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="discount_type"
                      name="discount_type"
                      value={formData.discount_type || DiscountType.FIXED_AMOUNT}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('discount_type')}
                      disabled={isSubmitting}
                      className={clsx(
                        'select',
                        errors.discount_type &&
                        'border-red-500 focus:ring-red-500'
                      )}
                      aria-invalid={!!errors.discount_type}
                      aria-describedby={
                        errors.discount_type ? 'discount-type-error' : undefined
                      }
                    >
                      <option value={DiscountType.FIXED_AMOUNT}>
                        Fixed Amount
                      </option>
                      <option value={DiscountType.PERCENTAGE}>Percentage</option>
                    </select>
                  </>
                )}
                {errors.discount_type && (
                  <span id="discount-type-error" className="text-2sm text-danger">
                    {errors.discount_type}
                  </span>
                )}
              </div>

              {/* Coupon Type */}
              <div className="flex flex-col gap-2">
                {mode === 'view' ? (
                  <>
                    <span
                      id="coupon_type-label"
                      className="text-sm font-medium text-gray-700"
                    >
                      Coupon Type <span className="text-red-500">*</span>
                    </span>
                    <div
                      id="coupon_type"
                      className="input bg-gray-100"
                      aria-labelledby="coupon_type-label"
                      role="text"
                    >
                      {getCouponTypeLabel(
                        formData.coupon_type as number | undefined
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="coupon_type"
                      className="text-sm font-medium text-gray-700"
                    >
                      Coupon Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="coupon_type"
                      name="coupon_type"
                      value={formData.coupon_type ?? ''}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('coupon_type' as keyof IPromoData)}
                      disabled={isSubmitting}
                      className={clsx(
                        'select',
                        errors.coupon_type && 'border-red-500 focus:ring-red-500'
                      )}
                    >
                      <option value="">Select coupon type</option>
                      <option value={CouponType.SUBSCRIPTION_BOATER}>
                        Subscription - Boater
                      </option>
                      <option value={CouponType.SUBSCRIPTION_HARBOR}>
                        Subscription - Harbor
                      </option>
                      <option value={CouponType.SUBSCRIPTION_CLUB}>
                        Subscription - Club
                      </option>
                      <option value={CouponType.SUBSCRIPTION_DMO}>
                        Subscription - DMO
                      </option>
                      <option value={CouponType.SUBSCRIPTION_SERVICE_PROVIDER}>
                        Subscription - Service Provider
                      </option>
                    </select>
                  </>
                )}
                {errors.coupon_type && (
                  <span id="coupon-type-error" className="text-2sm text-danger">
                    {errors.coupon_type}
                  </span>
                )}
              </div>

              {/* Eligibility Rule - Only show for Subscription - Boater */}
              {Number(formData.coupon_type) ===
                CouponType.SUBSCRIPTION_BOATER && (
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="eligibility_rule"
                      className="text-sm font-medium text-gray-700"
                    >
                      Eligibility Rule
                    </label>
                    {mode === 'view' ? (
                      <div className="input bg-gray-100">
                        {Number(formData.eligibility_rule) ===
                          CouponEligibilityRule.CLUB_MEMBER_ONLY
                          ? 'Club members only'
                          : 'No restriction'}
                      </div>
                    ) : (
                      <select
                        id="eligibility_rule"
                        name="eligibility_rule"
                        aria-label="Eligibility Rule"
                        value={
                          formData.eligibility_rule ?? CouponEligibilityRule.NONE
                        }
                        onChange={handleInputChange}
                        onBlur={() =>
                          handleBlur('eligibility_rule' as keyof IPromoData)
                        }
                        disabled={isSubmitting}
                        className="select"
                      >
                        <option value={CouponEligibilityRule.NONE}>
                          No restriction
                        </option>
                        <option value={CouponEligibilityRule.CLUB_MEMBER_ONLY}>
                          Club members only
                        </option>
                      </select>
                    )}
                  </div>
                )}

              {/* Linked Club - Only show when Eligibility Rule is Club members only */}
              {Number(formData.coupon_type) === CouponType.SUBSCRIPTION_BOATER &&
                Number(formData.eligibility_rule) ===
                CouponEligibilityRule.CLUB_MEMBER_ONLY && (
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="linked_club_id"
                      className="text-sm font-medium text-gray-700"
                    >
                      Linked Club <span className="text-red-500">*</span>
                    </label>
                    {mode === 'view' ? (
                      <div className="input bg-gray-100">
                        {clubs.find((club) => club.id === formData.linked_club_id)
                          ?.name || 'N/A'}
                      </div>
                    ) : (
                      <select
                        id="linked_club_id"
                        name="linked_club_id"
                        value={formData.linked_club_id || ''}
                        onChange={handleInputChange}
                        onBlur={() =>
                          handleBlur('linked_club_id' as keyof IPromoData)
                        }
                        disabled={isSubmitting || isLoadingClubs}
                        className={clsx(
                          'select',
                          errors.linked_club_id &&
                          'border-red-500 focus:ring-red-500'
                        )}
                        aria-invalid={!!errors.linked_club_id}
                        aria-describedby={
                          errors.linked_club_id
                            ? 'linked-club-id-error'
                            : undefined
                        }
                      >
                        <option value="">
                          {isLoadingClubs ? 'Loading clubs...' : 'Select club'}
                        </option>
                        {!isLoadingClubs &&
                          clubs.map((club) => (
                            <option key={club.id} value={club.id}>
                              {club.name}
                            </option>
                          ))}
                      </select>
                    )}
                    {errors.linked_club_id && (
                      <span
                        id="linked-club-id-error"
                        className="text-2sm text-danger"
                      >
                        {errors.linked_club_id}
                      </span>
                    )}
                  </div>
                )}

              {/* Discount Amount - Always visible, label changes based on type */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor={
                    Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? 'discount_percentage'
                      : 'discount_amount'
                  }
                  className="text-sm font-medium text-gray-700"
                >
                  {Number(formData.discount_type) === DiscountType.PERCENTAGE
                    ? 'Discount Percentage (%)'
                    : 'Discount Amount (€)'}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <PriceInput
                  id={
                    Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? 'discount_percentage'
                      : 'discount_amount'
                  }
                  name={
                    Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? 'discount_percentage'
                      : 'discount_amount'
                  }
                  value={
                    Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? (formData.discount_percentage ?? '')
                      : (formData.discount_amount ?? '')
                  }
                  onChange={(val) => {
                    const fieldName =
                      Number(formData.discount_type) === DiscountType.PERCENTAGE
                        ? 'discount_percentage'
                        : 'discount_amount';
                    setFormData((prev) => ({
                      ...prev,
                      [fieldName]: val,
                    }));
                    validateField(fieldName, val);
                  }}
                  disabled={isSubmitting || mode === 'view'}
                  inputClassName={clsx(
                    'input',
                    ((Number(formData.discount_type) ===
                      DiscountType.PERCENTAGE &&
                      errors.discount_percentage) ||
                      (Number(formData.discount_type) ===
                        DiscountType.FIXED_AMOUNT &&
                        errors.discount_amount)) &&
                    'border-red-500 focus:ring-red-500'
                  )}
                  error={
                    Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? errors.discount_percentage
                      : errors.discount_amount
                  }
                  placeholder={
                    Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? 'Enter discount percentage'
                      : 'Enter discount amount'
                  }
                  min="0"
                  max={
                    Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? '100'
                      : undefined
                  }
                  step="0.01"
                  aria-invalid={
                    !!(Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? errors.discount_percentage
                      : errors.discount_amount)
                  }
                  aria-label="Discount Amount"
                />
              </div>

              {/* Max Discount Amount - Only show for Percentage type */}
              {Number(formData.discount_type) === DiscountType.PERCENTAGE && (
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="max_discount"
                    className="text-sm font-medium text-gray-700"
                  >
                    Max Discount Amount (€){' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <PriceInput
                    id="max_discount"
                    name="max_discount"
                    value={formData.max_discount ?? ''}
                    onChange={(val) => {
                      setFormData((prev) => ({ ...prev, max_discount: val }));
                      validateField('max_discount', val);
                    }}
                    disabled={isSubmitting || mode === 'view'}
                    inputClassName={clsx(
                      'input',
                      errors.max_discount && 'border-red-500 focus:ring-red-500'
                    )}
                    error={errors.max_discount}
                    placeholder="Enter discount amount"
                    min="0"
                    step="0.01"
                    aria-label="Max Discount Amount"
                    aria-invalid={!!errors.max_discount}
                  />
                </div>
              )}

              {/* Min Order Value */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="min_order_value"
                  className="text-sm font-medium text-gray-700"
                >
                  Min Order Value (€) <span className="text-red-500">*</span>
                </label>
                <PriceInput
                  id="min_order_value"
                  name="min_order_value"
                  value={formData.min_order_value ?? ''}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      min_order_value: val,
                    }));
                    validateField('min_order_value', val);
                  }}
                  disabled={isSubmitting || mode === 'view'}
                  inputClassName={clsx(
                    'input',
                    errors.min_order_value && 'border-red-500 focus:ring-red-500'
                  )}
                  error={errors.min_order_value}
                  placeholder="Enter minimum order value"
                  min="0"
                  step="0.01"
                  aria-label="Min Order Value"
                  aria-invalid={!!errors.min_order_value}
                />
              </div>

              {/* Date Range */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="start_date"
                  className="text-sm font-medium text-gray-700"
                >
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="start_date"
                  type="date"
                  name="start_date"
                  aria-label="Start Date"
                  min={new Date().toISOString().split('T')[0]}
                  value={formatDateLocal(formData.start_date as string)}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('start_date')}
                  onKeyDown={handleDateKeyDown}
                  disabled={isSubmitting || mode === 'view'}
                  className={clsx(
                    'input',
                    errors.start_date && 'border-red-500 focus:ring-red-500'
                  )}
                  aria-invalid={!!errors.start_date}
                  aria-describedby={
                    errors.start_date ? 'start-date-error' : undefined
                  }
                />
                {errors.start_date && (
                  <span id="start-date-error" className="text-2sm text-danger">
                    {errors.start_date}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="end_date"
                  className="text-sm font-medium text-gray-700"
                >
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="end_date"
                  type="date"
                  name="end_date"
                  value={formatDateLocal(formData.end_date as string)}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('end_date')}
                  onKeyDown={handleDateKeyDown}
                  disabled={isSubmitting || mode === 'view'}
                  min={formatDateLocal(formData.start_date as string)}
                  aria-label="End Date"
                  className={clsx(
                    'input',
                    errors.end_date && 'border-red-500 focus:ring-red-500'
                  )}
                  aria-invalid={!!errors.end_date}
                  aria-describedby={
                    errors.end_date ? 'end-date-error' : undefined
                  }
                />
                {errors.end_date && (
                  <span id="end-date-error" className="text-2sm text-danger">
                    {errors.end_date}
                  </span>
                )}
              </div>

              {/* Total Quantity */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="total_quantity"
                  className="text-sm font-medium text-gray-700"
                >
                  Total Quantity
                </label>
                <NumberInput
                  id="total_quantity"
                  name="total_quantity"
                  value={formData.total_quantity ?? ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('total_quantity')}
                  disabled={isSubmitting || mode === 'view'}
                  aria-label="Total Quantity"
                  className={clsx(
                    'input',
                    errors.total_quantity && 'border-red-500 focus:ring-red-500'
                  )}
                  placeholder="Unlimited"
                  min="1"
                  aria-invalid={!!errors.total_quantity}
                  aria-describedby={
                    errors.total_quantity ? 'total-quantity-error' : undefined
                  }
                />
                {errors.total_quantity && (
                  <span
                    id="total-quantity-error"
                    className="text-2sm text-danger"
                  >
                    {errors.total_quantity}
                  </span>
                )}
              </div>

              {/* Customer Usage Limit */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="customer_usage_limit"
                  className="text-sm font-medium text-gray-700"
                >
                  Customer Usage Limit
                </label>
                <NumberInput
                  id="customer_usage_limit"
                  name="customer_usage_limit"
                  value={formData.customer_usage_limit ?? ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('customer_usage_limit')}
                  disabled={isSubmitting || mode === 'view'}
                  className={clsx(
                    'input',
                    errors.customer_usage_limit &&
                    'border-red-500 focus:ring-red-500'
                  )}
                  placeholder="Unlimited"
                  aria-label="Customer Usage Limit"
                  min="1"
                  aria-invalid={!!errors.customer_usage_limit}
                  aria-describedby={
                    errors.customer_usage_limit
                      ? 'customer-usage-limit-error'
                      : undefined
                  }
                />
                {errors.customer_usage_limit && (
                  <span
                    id="customer-usage-limit-error"
                    className="text-2sm text-danger"
                  >
                    {errors.customer_usage_limit}
                  </span>
                )}
              </div>

              {/* Status */}
              <div className="flex flex-col gap-2">
                {mode === 'view' ? (
                  <>
                    <span
                      id="status-label"
                      className="text-sm font-medium text-gray-700"
                    >
                      Status <span className="text-red-500">*</span>
                    </span>
                    <div
                      id="status"
                      className="input bg-gray-100"
                      aria-labelledby="status-label"
                      role="text"
                    >
                      {getStatusLabel(formData.status ?? Statuses.ACTIVE)}
                    </div>
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="status"
                      className="text-sm font-medium text-gray-700"
                    >
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status ?? Statuses.ACTIVE}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('status')}
                      disabled={isSubmitting}
                      className={clsx(
                        'select',
                        errors.status && 'border-red-500 focus:ring-red-500'
                      )}
                      aria-invalid={!!errors.status}
                      aria-describedby={
                        errors.status ? 'status-error' : undefined
                      }
                    >
                      <option value={Statuses.ACTIVE}>Active</option>
                      <option value={Statuses.INACTIVE}>Inactive</option>
                    </select>
                  </>
                )}
                {errors.status && (
                  <span id="status-error" className="text-2sm text-danger">
                    {errors.status}
                  </span>
                )}
              </div>

              {/* View mode additional info */}
              {mode === 'view' && (
                <div className="grid grid-cols-2 gap-2.5 pt-2.5 border-t">
                  <div className="flex flex-col gap-1">
                    <span
                      id="remaining_usage_count-label"
                      className="form-label text-gray-900"
                    >
                      Remaining Usage
                    </span>
                    <div
                      id="remaining_usage_count"
                      className="input bg-gray-100"
                      aria-labelledby="remaining_usage_count-label"
                      role="text"
                    >
                      {formData.remaining_usage_count || 0}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span
                      id="created_at-label"
                      className="form-label text-gray-900"
                    >
                      Created At
                    </span>
                    <div
                      id="created_at"
                      className="input bg-gray-100"
                      aria-labelledby="created_at-label"
                      role="text"
                    >
                      {formData.created_at
                        ? formatDisplayDate(
                          new Date(formData.created_at as string)
                        )
                        : 'N/A'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {mode !== 'view' && (
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-200 contrast:hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                onClick={onSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? 'Saving...'
                  : mode === 'create'
                    ? 'Create Coupon'
                    : 'Update Coupon'}
              </button>
            </div>
          )}</div>
      </div>
    </Modal>
  );
};

export { PromoModal };
